import { v4 as uuid } from 'uuid';

export interface CustomerSecretTarget {
  id: string;
  label: string;
  pathTail: string;
  configPath: readonly string[];
  secretKind: 'credentials' | 'apiKey';
}

export const customerSecretTargets: readonly CustomerSecretTarget[] = [
  {
    id: 'senpicSenbos',
    label: 'Senbos Api Secret',
    pathTail: 'senpic/senbos',
    configPath: ['Senpic', 'Senbos', 'ApiUsernamePasswordSecret'],
    secretKind: 'credentials',
  },
  {
    id: 'senpicStagingSenbos',
    label: 'Staging Senbos Api Secret',
    pathTail: 'senpic/stagingSenbos',
    configPath: ['Senpic', 'StagingSenbos', 'ApiUsernamePasswordSecret'],
    secretKind: 'credentials',
  },
  {
    id: 'senshieldSenbos',
    label: 'Senshield Senbos Api Secret',
    pathTail: 'senshield/senbos',
    configPath: ['Senshield', 'Senbos', 'ApiUsernamePasswordSecret'],
    secretKind: 'credentials',
  },
  {
    id: 'senforceSenbos',
    label: 'Senforce Senbos Api Secret',
    pathTail: 'senforce/senbos',
    configPath: ['Senforce', 'Senbos', 'ApiUsernamePasswordSecret'],
    secretKind: 'credentials',
  },
  {
    id: 'alertReviewerSenbos',
    label: 'Alert Reviewer Senbos Api Secret',
    pathTail: 'alertReviewer/senbos',
    configPath: ['AlertReviewer', 'Senbos', 'ApiUsernamePasswordSecret'],
    secretKind: 'credentials',
  },
  {
    id: 'alertReviewerGptApiKey',
    label: 'GPT API Key',
    pathTail: 'alertReviewer/gpt/apiKey',
    configPath: ['AlertReviewer', 'Gpt', 'ApiKey'],
    secretKind: 'apiKey',
  },
  {
    id: 'alertReviewerClaudeApiKey',
    label: 'Claude API Key',
    pathTail: 'alertReviewer/claude/apiKey',
    configPath: ['AlertReviewer', 'Claude', 'ApiKey'],
    secretKind: 'apiKey',
  },
  {
    id: 'alertReviewerGeminiApiKey',
    label: 'Gemini API Key',
    pathTail: 'alertReviewer/gemini/apiKey',
    configPath: ['AlertReviewer', 'Gemini', 'ApiKey'],
    secretKind: 'apiKey',
  },
];

// The fields the VLM gateway reads from a profile.
export const VLM_PROFILE_FIELDS = [
  'Provider',
  'ApiKey',
  'Model',
  'Prompt',
  'SystemPrompt',
  'ResponseSchema',
  'MaxTokens',
  'MaxImageDimension',
  'TimeoutSeconds',
  'ProviderOptions',
] as const;

export type VlmProfileBlockedReason =
  | 'invalidId'
  | 'idClash'
  | 'nameClash'
  | 'keyCasing';

export interface VlmGatewayProfileTarget extends CustomerSecretTarget {
  profileName: string;
  // Names the profile's key, so a rename keeps it.
  profileId: string;
  provider: string;
  model: string;
  blockedReason: VlmProfileBlockedReason | null;
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// senhub's path segment rule; `{name}` in `vlmGateway/{name}/apiKey` is capped at 40.
const VLM_PATH_SEGMENT_PATTERN = /^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/;
const VLM_PATH_SEGMENT_MAX_LENGTH = 40;

const isVlmPathSegment = (value: string): boolean =>
  value.length >= 1 &&
  value.length <= VLM_PATH_SEGMENT_MAX_LENGTH &&
  VLM_PATH_SEGMENT_PATTERN.test(value);

// The key path holds the id, not the name, but names follow the same rule.
export const isValidVlmProfileId = isVlmPathSegment;
export const isValidVlmProfileName = isVlmPathSegment;

export const newVlmProfileId = (): string => uuid().replaceAll('-', '');

const hasMiscasedKey = (
  record: Record<string, unknown>,
  expected: string,
): boolean => {
  const lowerExpected = expected.toLowerCase();
  return Object.keys(record).some(
    (key) => key !== expected && key.toLowerCase() === lowerExpected,
  );
};

const lowerVlmProfileFields = new Map<string, string>(
  [...VLM_PROFILE_FIELDS, 'Id'].map((field) => [field.toLowerCase(), field]),
);

export const hasMiscasedVlmProfileField = (profile: unknown): boolean =>
  isRecord(profile) &&
  Object.keys(profile).some((key) => {
    const field = lowerVlmProfileFields.get(key.toLowerCase());
    return field !== undefined && field !== key;
  });

// Exact keys only: the console never reads a differently cased section.
export const getVlmGatewayProfiles = (
  config: unknown,
): Record<string, unknown> => {
  if (!isRecord(config) || !isRecord(config.VlmGateway)) {
    return {};
  }

  const profiles = config.VlmGateway.Profiles;
  return isRecord(profiles) ? profiles : {};
};

const readString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const readProfileId = (profile: unknown): string =>
  isRecord(profile) ? readString(profile.Id) : '';

const countIgnoringCase = (values: readonly string[]): Map<string, number> =>
  values.reduce<Map<string, number>>((t, value) => {
    const lower = value.toLowerCase();
    t.set(lower, (t.get(lower) ?? 0) + 1);
    return t;
  }, new Map());

export const vlmGatewayProfileTargets = (
  config: unknown,
): VlmGatewayProfileTarget[] => {
  const profiles = getVlmGatewayProfiles(config);
  const names = Object.keys(profiles).sort((a, b) => a.localeCompare(b, 'en'));
  const lowerNameCounts = countIgnoringCase(names);
  // Key Vault names ignore case, so ids that differ only in case share a key.
  const lowerIdCounts = countIgnoringCase(
    names.map((name) => readProfileId(profiles[name])),
  );

  return names.map((name) => {
    const profile = profiles[name];
    const id = readProfileId(profile);
    // Casing first: a profile with a miscased field gets no id until fixed.
    const blockedReason: VlmProfileBlockedReason | null =
      hasMiscasedVlmProfileField(profile)
        ? 'keyCasing'
        : !isValidVlmProfileId(id)
          ? 'invalidId'
          : (lowerIdCounts.get(id.toLowerCase()) ?? 0) > 1
            ? 'idClash'
            : (lowerNameCounts.get(name.toLowerCase()) ?? 0) > 1
              ? 'nameClash'
              : null;

    return vlmGatewayProfileTarget(name, profile, blockedReason);
  });
};

export const vlmGatewayProfileTargetId = (profileId: string): string =>
  `vlmGateway:${profileId}`;

export const vlmGatewayProfileTarget = (
  name: string,
  profile: unknown,
  blockedReason: VlmProfileBlockedReason | null,
): VlmGatewayProfileTarget => {
  const profileId = readProfileId(profile);
  return {
    id: vlmGatewayProfileTargetId(profileId),
    label: `${name} API Key`,
    pathTail: `vlmGateway/${profileId}/apiKey`,
    configPath: ['VlmGateway', 'Profiles', name, 'ApiKey'],
    secretKind: 'apiKey',
    profileName: name,
    profileId,
    provider: isRecord(profile) ? readString(profile.Provider) : '',
    model: isRecord(profile) ? readString(profile.Model) : '',
    blockedReason,
  };
};

// Unset, null or empty; any other value is kept, and blocked if it can't be an Id.
const lacksProfileId = (profile: Record<string, unknown>): boolean =>
  profile.Id === undefined || profile.Id === null || profile.Id === '';

// A stable stand-in for a stored name the key path can't hold, so the Id
// doesn't change between loads before it is saved.
const vlmProfileIdFromName = (name: string): string =>
  name
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 40)
    .replace(/-+$/, '');

// Gives every profile without an Id a new one. With `storedNames`, which is
// only for profiles stored before ids existed, a profile takes its name,
// where its key already lives, or a stable form of it. Explicit ids never
// change.
export const withVlmProfileIds = (
  config: unknown,
  {
    storedNames = false,
    newId = newVlmProfileId,
  }: { storedNames?: boolean; newId?: () => string } = {},
): unknown => {
  if (
    !isRecord(config) ||
    hasMiscasedVlmGatewaySection(config) ||
    hasMalformedVlmGatewaySection(config)
  ) {
    return config;
  }

  const profiles = getVlmGatewayProfiles(config);
  // A profile with a miscased field stays as it is, so no second Id appears.
  const missing = Object.keys(profiles).filter((name) => {
    const profile = profiles[name];
    return (
      isRecord(profile) &&
      !hasMiscasedVlmProfileField(profile) &&
      lacksProfileId(profile)
    );
  });
  if (missing.length === 0) {
    return config;
  }

  const taken = new Set(
    Object.values(profiles)
      .map((profile) => readProfileId(profile).toLowerCase())
      .filter((id) => id !== ''),
  );
  const assigned = new Map<string, string>();
  const claim = (name: string, id: string): boolean => {
    if (!isValidVlmProfileId(id) || taken.has(id.toLowerCase())) {
      return false;
    }
    taken.add(id.toLowerCase());
    assigned.set(name, id);
    return true;
  };
  // Exact names first: they are where existing keys live.
  if (storedNames) {
    missing.forEach((name) => claim(name, name));
  }
  missing
    .filter((name) => !assigned.has(name))
    .forEach((name) => {
      if (!storedNames || !claim(name, vlmProfileIdFromName(name))) {
        let id = newId();
        while (!claim(name, id)) {
          id = newId();
        }
      }
    });

  const nextProfiles = Object.fromEntries(
    Object.entries(profiles).map(([name, profile]) => {
      const id = assigned.get(name);
      if (id === undefined || !isRecord(profile)) {
        return [name, profile];
      }
      // Id goes first so it reads first in JSON mode; an empty one is replaced.
      const { Id: _empty, ...rest } = profile;
      return [name, { Id: id, ...rest }];
    }),
  );
  return {
    ...config,
    VlmGateway: { ...(config.VlmGateway as object), Profiles: nextProfiles },
  };
};

// Profile ids used more than once, ignoring case; such profiles share a key.
export const duplicateVlmProfileIds = (config: unknown): string[] => {
  const ids = Object.values(getVlmGatewayProfiles(config))
    .map(readProfileId)
    .filter((id) => id !== '');
  const counts = countIgnoringCase(ids);
  return [
    ...new Set(ids.filter((id) => (counts.get(id.toLowerCase()) ?? 0) > 1)),
  ].sort((a, b) => a.localeCompare(b, 'en'));
};

export const hasMiscasedVlmGatewaySection = (config: unknown): boolean => {
  if (!isRecord(config)) {
    return false;
  }

  if (hasMiscasedKey(config, 'VlmGateway')) {
    return true;
  }

  const gateway = config.VlmGateway;
  return isRecord(gateway) && hasMiscasedKey(gateway, 'Profiles');
};

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null;

// A section Form mode can't add profiles to without overwriting it.
export const hasMalformedVlmGatewaySection = (config: unknown): boolean => {
  if (!isRecord(config) || !isPresent(config.VlmGateway)) {
    return false;
  }

  const gateway = config.VlmGateway;
  return (
    !isRecord(gateway) ||
    (isPresent(gateway.Profiles) && !isRecord(gateway.Profiles))
  );
};

// With a case variant beside the exact section, the gateway may read either
// (duplicate keys resolve by jsonb key order), so no profile is manageable.
export const manageableCustomerSecretTargets = (
  config: unknown,
): CustomerSecretTarget[] =>
  hasMiscasedVlmGatewaySection(config)
    ? [...customerSecretTargets]
    : [
        ...customerSecretTargets,
        ...vlmGatewayProfileTargets(config).filter(
          (target) => target.blockedReason === null,
        ),
      ];

export const getConfigValueAtPath = (
  config: unknown,
  path: readonly string[],
): string => {
  const value = path.reduce<unknown>(
    (t, key) => (isRecord(t) ? t[key] : undefined),
    config,
  );
  return readString(value);
};

// Key targets of the profiles whose stored key the console manages, by name.
const consoleKeyTargets = (
  config: unknown,
): Map<string, CustomerSecretTarget> =>
  hasMiscasedVlmGatewaySection(config)
    ? new Map<string, CustomerSecretTarget>()
    : new Map(
        vlmGatewayProfileTargets(config)
          .filter(
            (target) =>
              target.blockedReason === null &&
              getConfigValueAtPath(config, target.configPath).trim() !== '',
          )
          .map((target) => [target.profileName, target]),
      );

// Every profile's ApiKey reference, so a key one of them still uses is kept.
export const vlmProfileApiKeyReferences = (config: unknown): Set<string> =>
  new Set(
    Object.values(getVlmGatewayProfiles(config))
      .map((profile) => (isRecord(profile) ? readString(profile.ApiKey) : ''))
      .map((reference) => reference.trim())
      .filter((reference) => reference !== ''),
  );

export type VlmProfileRemoval = {
  name: string;
  // Set when removing the profile also deletes its console-managed key.
  keyTarget: CustomerSecretTarget | undefined;
};

export const vlmProfileRemovals = (
  config: unknown,
  names: readonly string[],
): VlmProfileRemoval[] => {
  const profiles = getVlmGatewayProfiles(config);
  const keyTargets = consoleKeyTargets(config);
  return names
    .filter((name) => Object.hasOwn(profiles, name))
    .map((name) => ({ name, keyTarget: keyTargets.get(name) }));
};

// A rename moves only the profile's name; its Id, and so its key, stay.
export type VlmProfileRename = {
  from: string;
  to: string;
};

export const vlmProfileRenames = (
  config: unknown,
  renames: Record<string, string>,
): VlmProfileRename[] => {
  const profiles = getVlmGatewayProfiles(config);
  const names = Object.keys(profiles);
  // Clashes are settled here only: renameVlmProfiles applies these as given.
  const taken = new Set<string>();
  return Object.entries(renames)
    .filter(([from, to]) => {
      const lowerTo = to.toLowerCase();
      const applicable =
        Object.hasOwn(profiles, from) &&
        from !== to &&
        isValidVlmProfileName(to) &&
        !taken.has(lowerTo) &&
        !names.some(
          (other) => other !== from && other.toLowerCase() === lowerTo,
        );
      if (applicable) {
        taken.add(lowerTo);
      }
      return applicable;
    })
    .map(([from, to]) => ({ from, to }));
};

type CustomerSecretDraftNone = {
  action: 'none';
};

type CustomerSecretDraftUpsert = {
  action: 'upsert';
  username: string;
  password: string;
};

type CustomerSecretDraftDelete = {
  action: 'delete';
};

export type CustomerSecretDraft =
  | CustomerSecretDraftNone
  | CustomerSecretDraftUpsert
  | CustomerSecretDraftDelete;

export type CustomerSecretDrafts = Record<string, CustomerSecretDraft>;

export const getCustomerSecretDraft = (
  drafts: CustomerSecretDrafts,
  id: string,
): CustomerSecretDraft => drafts[id] ?? { action: 'none' };

export const createSecretUpsertDraft = (
  credentials: { username: string; password: string } | { apiKey: string },
): CustomerSecretDraftUpsert => ({
  action: 'upsert',
  username: 'apiKey' in credentials ? '' : credentials.username,
  password: 'apiKey' in credentials ? credentials.apiKey : credentials.password,
});

export const createDefaultCustomerSecretDrafts =
  (): CustomerSecretDrafts => ({});

export const getCustomerSecretStatusText = (
  draft: CustomerSecretDraft,
): string => {
  if (draft.action === 'upsert') {
    return 'Secret will update when Save is clicked';
  }

  if (draft.action === 'delete') {
    return 'Secret will be deleted when Save is clicked';
  }

  return '';
};

// Key writes, made before the config save so config can point at them.
export type CustomerSecretOperation = {
  target: CustomerSecretTarget;
  secretJson: string;
};

export const buildCustomerSecretOperations = (
  drafts: CustomerSecretDrafts,
  targets: readonly CustomerSecretTarget[],
): CustomerSecretOperation[] =>
  targets.flatMap((target) => {
    const draft = getCustomerSecretDraft(drafts, target.id);
    if (draft.action !== 'upsert') {
      return [];
    }

    const secretJson =
      target.secretKind === 'apiKey'
        ? JSON.stringify({
            apiKey: draft.password,
          })
        : JSON.stringify({
            username: draft.username,
            password: draft.password,
          });
    return [{ target, secretJson }];
  });

// Deleted only after config drops their references, so a failed config save
// never leaves config pointing at a deleted key.
export const stagedCustomerSecretDeletes = (
  drafts: CustomerSecretDrafts,
  targets: readonly CustomerSecretTarget[],
): CustomerSecretTarget[] =>
  targets.filter(
    (target) => getCustomerSecretDraft(drafts, target.id).action === 'delete',
  );

const setValueAtPath = (
  value: Record<string, unknown>,
  path: readonly string[],
  nextValue: string,
): Record<string, unknown> => {
  if (path.length === 0) {
    return value;
  }

  const [key, ...rest] = path;
  if (rest.length === 0) {
    return {
      ...value,
      [key]: nextValue,
    };
  }

  const next = value[key];
  const nextObject =
    next && typeof next === 'object' && !Array.isArray(next)
      ? (next as Record<string, unknown>)
      : {};

  return {
    ...value,
    [key]: setValueAtPath(nextObject, rest, nextValue),
  };
};

export const applyCustomerSecretConfigValues = (
  config: unknown,
  values: Record<string, string>,
  targets: readonly CustomerSecretTarget[],
): unknown => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return config;
  }

  return targets.reduce<Record<string, unknown>>(
    (t, target) => {
      const nextValue = values[target.id];
      if (typeof nextValue !== 'string') {
        return t;
      }

      return setValueAtPath(t, target.configPath, nextValue);
    },
    { ...(config as Record<string, unknown>) },
  );
};

const clearSecretAtPath = (
  value: Record<string, unknown>,
  path: readonly string[],
): Record<string, unknown> => {
  if (path.length === 0) {
    return value;
  }

  const [key, ...rest] = path;
  if (!(key in value)) {
    return value;
  }

  if (rest.length === 0) {
    return {
      ...value,
      [key]: '',
    };
  }

  const next = value[key];
  if (!next || typeof next !== 'object' || Array.isArray(next)) {
    return value;
  }

  return {
    ...value,
    [key]: clearSecretAtPath(next as Record<string, unknown>, rest),
  };
};

const updateKeysIgnoringCase = (
  record: Record<string, unknown>,
  name: string,
  update: (value: unknown) => unknown,
): Record<string, unknown> => {
  const lowerName = name.toLowerCase();
  return Object.keys(record).reduce(
    (t, key) =>
      key.toLowerCase() === lowerName
        ? { ...t, [key]: update(record[key]) }
        : t,
    record,
  );
};

const updateRecordValues = (
  record: Record<string, unknown>,
  update: (value: unknown) => unknown,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, update(value)]),
  );

// The gateway reads these keys case-insensitively, so every spelling is cleared.
const stripVlmGatewayApiKeys = (
  config: Record<string, unknown>,
): Record<string, unknown> =>
  updateKeysIgnoringCase(config, 'VlmGateway', (gateway) =>
    isRecord(gateway)
      ? updateKeysIgnoringCase(gateway, 'Profiles', (profiles) =>
          isRecord(profiles)
            ? updateRecordValues(profiles, (profile) =>
                isRecord(profile)
                  ? updateKeysIgnoringCase(profile, 'ApiKey', () => '')
                  : profile,
              )
            : profiles,
        )
      : gateway,
  );

export const stripCustomerSecretValues = (config: unknown): unknown => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return config;
  }

  return stripVlmGatewayApiKeys(
    customerSecretTargets.reduce<Record<string, unknown>>(
      (t, target) => {
        return clearSecretAtPath(t, target.configPath);
      },
      { ...(config as Record<string, unknown>) },
    ),
  );
};
